/**
 * Stock availability. **Pure** — no DOM, no SDK, no config.
 *
 * 🚨 Reachable from `@cod/form-tree/pure`, and deliberately so, for the same reason as
 * `offers.logic.ts` and `calendar.ts`: both bundles ship at once and both must answer "can this be
 * bought?" identically. A product that hides the COD button on Patagonia and offers it on Amazonas
 * is the same store taking orders it cannot fulfil on half its traffic.
 *
 * ## Why the COD surface has to care at all
 *
 * The theme disables its own add-to-cart button for a sold-out variant, but our button is a
 * separate control anchored beside it — nothing about the theme's state reaches it. So the buyer
 * saw a live "Comprar con envío" next to a dead buy button, and the order it created was one the
 * merchant had no stock for.
 *
 * ## The two runtimes describe stock differently, and neither is complete
 *
 * | Source                     | Fields                                      |
 * | -------------------------- | ------------------------------------------- |
 * | `LS.variants[]` (legacy)   | `available: boolean`, `stock: number｜null` |
 * | `ProductVariant` (NubeSDK) | `stock_management: boolean`, `stock: number｜null` |
 *
 * `LS` carries the theme's own verdict and no stock-management flag; NubeSDK carries the flag and
 * no verdict. `StockInfo` is the union of the three fields so one rule reads both, and the rule
 * below is ordered so each source only consults what it actually has.
 *
 * ## Unknown means AVAILABLE — this fails open on purpose
 *
 * Every branch that cannot decide returns `false`. A wrong "out of stock" removes the COD form
 * from a product the merchant CAN sell, which is silent lost revenue on a surface they cannot
 * inspect; a wrong "in stock" costs one order the merchant cancels, which they can see and
 * handle. Themes vary and both payload shapes are partly undocumented, so this asymmetry decides
 * every tie.
 */
/**
 * Whether this variant cannot be bought right now.
 *
 * Ordered most-authoritative first:
 *
 *  1. **`available`** — the theme has already applied the store's own rules (stock management,
 *     "keep selling when out of stock", per-variant publication) and this is its answer. When it
 *     is present it wins outright, including over a contradictory `stock`, because it is exactly
 *     what drove the buy button the buyer is looking at.
 *  2. **`managed === false`** — stock is untracked, so the count below means nothing. Catalogs do
 *     return `stock: 0` alongside it, and reading that as sold out hid the form on products with
 *     unlimited stock.
 *  3. **`stock`** — a tracked count at or below zero.
 *
 * Anything else: available (see the header on failing open).
 */
export function isOutOfStock(info) {
    if (!info)
        return false;
    if (info.available === false)
        return true;
    if (info.available === true)
        return false;
    if (info.managed === false)
        return false;
    return typeof info.stock === 'number' && info.stock <= 0;
}
/**
 * The variant ids the live catalogue says cannot be bought.
 *
 * Empty whenever the context carries no stock information at all — the admin preview and every
 * cart context, which have no `product.variants` to read. That is the fail-open case again: no
 * knowledge disables nothing.
 *
 * 🚨 Read from `ctx.product.variants` (the LIVE catalogue), while the offer pickers list the
 * offer's CAPTURED variants (recorded in the admin, see `offerVariantSource`). The two are matched
 * by id, which is what lets a capture taken months ago still grey out what sold out since.
 */
export function outOfStockVariantIds(ctx) {
    const ids = new Set();
    for (const v of ctx.product.variants ?? [])
        if (v.outOfStock)
            ids.add(v.id);
    return ids;
}
/**
 * The suffix appended to a sold-out option's label.
 *
 * Hardcoded Spanish, like `CALENDAR_TEXT` — the storefront form has no locale table, and every
 * buyer-facing string this repo owns rather than the merchant follows that convention.
 *
 * Greying an option out communicates "you cannot pick this" but not WHY, and a dropdown with three
 * dead entries and no explanation reads as broken rather than as sold out. The disabled state is
 * what enforces it; this is what makes it legible.
 */
export const SOLD_OUT_LABEL = 'Sin stock';
/** An option's label with the sold-out suffix when it applies. */
export function withSoldOutLabel(label, soldOut) {
    return soldOut ? `${label} — ${SOLD_OUT_LABEL}` : label;
}
//# sourceMappingURL=stock.js.map